import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Loader2, XCircle } from "lucide-react";
import { api } from "../lib/api";
import { fmt, DELIVERY_NOTICE } from "../data/site";
import { HalalBadge } from "../components/Navbar";

const readLastOrder = () => {
  try {
    return JSON.parse(sessionStorage.getItem("hiplate-last-order") || "null");
  } catch {
    return null;
  }
};

export const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const last = readLastOrder();

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          if (!cancelled) setState("paid");
          return;
        }
      } catch {}
      attempts += 1;
      if (!cancelled) {
        if (attempts < 15) setTimeout(poll, 2000);
        else setState("timeout");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <main className="grain flex min-h-[80vh] items-center justify-center bg-parchment px-4 pt-32 pb-20" data-testid="payment-success-page">
      <div className="w-full max-w-lg rounded-3xl border border-inkline bg-cream p-10 text-center">
        {state === "checking" && (
          <>
            <Loader2 size={40} className="mx-auto animate-spin text-spice" />
            <h1 className="mt-6 font-display text-3xl font-semibold text-ink">Confirming your payment…</h1>
            <p className="mt-3 text-sm text-muted2" data-testid="payment-status-text">Hold tight — we're checking with Stripe.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <span className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-halal/15">
              <Check size={36} className="text-halal" />
            </span>
            <h1 className="mt-6 font-display text-3xl font-semibold text-ink" data-testid="payment-status-text">Payment confirmed.</h1>
            <p className="mt-3 text-sm leading-relaxed text-muted2">
              {last ? `Your ${last.meals}-meal bundle (${fmt(last.total)}) is paid and with our Woodbridge kitchen.` : "Your order is paid and with our Woodbridge kitchen."}
            </p>
            <p className="mt-2 text-xs text-muted2">{DELIVERY_NOTICE}</p>
            <div className="mt-5 flex justify-center"><HalalBadge /></div>
            <Link to="/meal-prep" data-testid="payment-success-cta" className="mt-8 inline-block rounded-full bg-ink px-7 py-3.5 text-sm font-semibold text-parchment transition-colors hover:bg-spice">
              Build Another Bundle
            </Link>
          </>
        )}
        {(state === "failed" || state === "timeout") && (
          <>
            <XCircle size={40} className="mx-auto text-spice" />
            <h1 className="mt-6 font-display text-3xl font-semibold text-ink" data-testid="payment-status-text">
              {state === "timeout" ? "Still processing…" : "We couldn't confirm that payment."}
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-muted2">
              If your card was charged, your order is safe — call us at (571) 833-1906 and we'll confirm it by phone.
            </p>
            <Link to="/meal-prep" data-testid="payment-retry-cta" className="mt-8 inline-block rounded-full bg-ink px-7 py-3.5 text-sm font-semibold text-parchment transition-colors hover:bg-spice">
              Back to the Menu
            </Link>
          </>
        )}
      </div>
    </main>
  );
};

export const PaymentCancel = () => (
  <main className="grain flex min-h-[80vh] items-center justify-center bg-parchment px-4 pt-32 pb-20" data-testid="payment-cancel-page">
    <div className="w-full max-w-lg rounded-3xl border border-inkline bg-cream p-10 text-center">
      <XCircle size={40} className="mx-auto text-spice" />
      <h1 className="mt-6 font-display text-3xl font-semibold text-ink">Payment cancelled.</h1>
      <p className="mt-3 text-sm leading-relaxed text-muted2">
        No charge was made. Your bundle is still waiting whenever you're ready.
      </p>
      <Link to="/meal-prep" data-testid="payment-cancel-cta" className="mt-8 inline-block rounded-full bg-spice px-7 py-3.5 text-sm font-bold text-parchment transition-colors hover:bg-ink">
        Return to This Week's Menu
      </Link>
    </div>
  </main>
);
